import React from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { TextInput } from "@/components/ui/TextInput";
import { PreviewSection } from "../shared";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { CourseType } from "@/types/db/courses";

export interface CourseOverviewProps {
  course: CourseType | null;
  loading?: boolean;
}

export function CourseOverviewTab({ course, loading = false }: CourseOverviewProps) {
  const { editing, formData, updateFormData } = useEntityEdit<CourseType>();

  // Unified data source
  const displayData = editing ? formData : course;

  // Calculate content statistics
  const sectionCount = displayData?.meta_data?.sections?.length || 0;
  const totalQuestions =
    displayData?.meta_data?.sections?.reduce((acc, section) => acc + (section.questions?.length || 0), 0) || 0;
  const totalPoints =
    displayData?.meta_data?.sections?.reduce(
      (acc, section) => acc + (section.questions?.reduce((qAcc, q) => qAcc + (q.points || 0), 0) || 0),
      0,
    ) || 0;

  return (
    <View style={styles.content}>
      <PreviewSection title="Course Details">
        <TextInput
          label="Name"
          value={displayData?.name || ""}
          onChangeText={(text) => updateFormData({ ...formData, name: text })}
          editable={editing}
          loading={loading}
        />
        <TextInput
          label="Open Enrollment"
          value={displayData?.open_enrollment ? "Yes" : "No"}
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
        <TextInput
          label="Archived"
          value={displayData?.archived ? "Yes" : "No"}
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
      </PreviewSection>

      <PreviewSection title="Description">
        <TextInput
          label="Description"
          value={displayData?.description || ""}
          onChangeText={(text) => updateFormData({ ...formData, description: text })}
          editable={editing}
          loading={loading}
          multiline
          numberOfLines={3}
        />
      </PreviewSection>

      <PreviewSection title="Course Content">
        <TextInput
          label="Sections"
          value={sectionCount.toString()}
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
        <TextInput
          label="Total Questions"
          value={totalQuestions.toString()}
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
        <TextInput
          label="Total Points"
          value={totalPoints.toString()}
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
      </PreviewSection>

      <PreviewSection title="Settings">
        <TextInput
          label="Passing Score"
          value={
            displayData?.meta_data?.settings?.passingScore ? `${displayData.meta_data.settings.passingScore}%` : "—"
          }
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
        <TextInput
          label="Max Attempts"
          value={
            displayData?.meta_data?.settings?.maxAttempts === null
              ? "Unlimited"
              : displayData?.meta_data?.settings?.maxAttempts?.toString() || "—"
          }
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
        <TextInput
          label="Time Limit"
          value={
            displayData?.meta_data?.settings?.timeLimit
              ? `${displayData.meta_data.settings.timeLimit} minutes`
              : "No limit"
          }
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
        <TextInput
          label="Certificate"
          value={displayData?.meta_data?.settings?.certificateEnabled ? "Enabled" : "Disabled"}
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
        <TextInput
          label="Show Answers"
          value={
            displayData?.meta_data?.settings?.showCorrectAnswers
              ? `After ${displayData.meta_data.settings.showCorrectAnswersAfter}`
              : "Never"
          }
          onChangeText={() => {}}
          editable={false}
          loading={loading}
        />
      </PreviewSection>

      {displayData?.meta_data?.sections && displayData.meta_data.sections.length > 0 && (
        <PreviewSection title="Sections">
          {displayData.meta_data.sections.map((section, index) => (
            <View key={section.id} style={styles.sectionCard}>
              <ThemedText type="defaultSemiBold">
                {index + 1}. {section.title}
              </ThemedText>
              {section.description && (
                <ThemedText style={{ opacity: 0.7, fontSize: 12 }}>{section.description}</ThemedText>
              )}
              <ThemedText style={{ opacity: 0.6, fontSize: 12, marginTop: 4 }}>
                {section.questions?.length || 0} questions •{" "}
                {section.questions?.reduce((acc, q) => acc + (q.points || 0), 0) || 0} points
                {section.requiredToPass && " • Required"}
              </ThemedText>
            </View>
          ))}
        </PreviewSection>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 16,
    gap: 16,
    overflowY: "scroll",
  },
  sectionCard: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    marginBottom: 8,
    gap: 4,
  },
});
